import { LifeBuoy, LogOut } from 'lucide-react';
import { cn } from '../lib/cn';
import { SearchableSelect } from './SearchableSelect';

export interface SupportBarOrganisation {
  id: string;
  name: string;
}

export interface SupportBarAccount {
  id: string;
  name: string;
  organisations: SupportBarOrganisation[];
}

export interface SupportBarProps {
  /**
   * Whether the signed-in user is Stratum support at all. False for every
   * customer, in which case this renders nothing.
   */
  isSupportUser: boolean;
  /** Every account on the platform. Empty until the list resolves. */
  accounts: SupportBarAccount[];
  /** The account currently being worked in, or `''` when none is selected. */
  currentAccountId: string;
  /** The organisation currently being worked in, or `''`. */
  currentOrganisationId: string;
  /** Pick an account and organisation to act in. */
  onSelect: (accountId: string, organisationId: string) => void;
  /**
   * Leave. This SIGNS THE USER OUT — moving between customers is what the two
   * dropdowns are for, so the only thing left for a button to mean is "I am
   * done". Labelled accordingly, because a one-click silent logout would be a
   * nasty surprise.
   */
  onExit: () => void;
  isLoading?: boolean;
  className?: string;
}

/**
 * The always-visible reminder of whose data is on screen during a Stratum
 * support session, and the picker for changing it.
 *
 * <p>
 * IT IS LOUD ON PURPOSE. A support session has unrestricted write access to a
 * customer's live data, and the screens underneath look exactly like the
 * customer's own. The one thing that must never happen is a support user
 * editing a customer's records believing they are somewhere else, so the bar
 * is full-width, high-contrast, and cannot be dismissed — only exited.
 * </p>
 *
 * <p>
 * Rendered through `DashboardLayout`'s `banner` slot, which spans the full
 * width UNDER the sidebar and takes its own space at the bottom of the shell.
 * It is not fixed or sticky on purpose — see the note on that prop.
 * </p>
 *
 * <p>
 * Purely presentational, like `AccountSwitcher`: it takes props and calls
 * back. The wiring (fetching accounts, writing the session, clearing cached
 * customer data) is `useSupportBar()` in `@my/directory-api/hooks`.
 * </p>
 */
export function SupportBar({
  isSupportUser,
  accounts,
  currentAccountId,
  currentOrganisationId,
  onSelect,
  onExit,
  isLoading = false,
  className,
}: SupportBarProps) {
  // Nothing at all for a customer. Not a hidden element, not a zero-height
  // container — a support surface should leave no trace in a screen somebody
  // might screenshot and send us.
  if (!isSupportUser) return null;

  const account = accounts.find((a) => a.id === currentAccountId);
  const organisations = account?.organisations ?? [];
  const active = Boolean(currentAccountId && currentOrganisationId);

  const selectClasses =
    'rounded border border-white/30 bg-white/10 px-2 py-1 text-sm text-white ' +
    'outline-none focus:border-white/60 disabled:opacity-50 [&>option]:text-slate-900';

  return (
    <div
      role="region"
      aria-label="Stratum support session"
      className={cn(
        'flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2',
        active ? 'bg-rose-700 text-white' : 'bg-slate-700 text-white',
        className,
      )}
    >
      <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden />

      <span className="text-sm font-semibold">
        {active ? 'Support session' : 'Support — no account selected'}
      </span>

      {/* Searchable: this picks from every account on the platform, and a
          native dropdown over thousands is a scroll rather than a choice. */}
      <SearchableSelect
        label="Customer account"
        placeholder={isLoading ? 'Loading…' : 'Select account…'}
        searchPlaceholder="Search accounts…"
        disabled={isLoading}
        dropUp
        options={accounts.map((a) => ({ value: a.id, label: a.name }))}
        value={currentAccountId}
        onChange={(accountId) => {
          // Changing account invalidates the organisation, so select the
          // account's first organisation rather than carrying a stale id from
          // the previous customer into the new one.
          const next = accounts.find((a) => a.id === accountId);
          onSelect(accountId, next?.organisations[0]?.id ?? '');
        }}
      />

      <select
        aria-label="Organisation"
        className={selectClasses}
        value={currentOrganisationId}
        disabled={!currentAccountId || organisations.length === 0}
        onChange={(e) => onSelect(currentAccountId, e.target.value)}
      >
        <option value="">Select organisation…</option>
        {organisations.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>

      {active && (
        <span className="text-sm">
          You are acting with full access in another company&rsquo;s live data.
        </span>
      )}

      {/* Never disabled — even with no customer selected, this is the way out
          of a support session. */}
      <button
        type="button"
        onClick={onExit}
        className={cn(
          'ml-auto inline-flex items-center gap-1 rounded border border-white/40 px-2 py-1',
          'text-sm hover:bg-white/10',
        )}
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden />
        Exit &amp; sign out
      </button>
    </div>
  );
}
